import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import SearchCompaniesList from '@/components/search/content/SearchCompaniesList';
import { CompanySearchResult } from '@/apiTypes/types';
import Paginator from '@/components/ui/Paginator';
import { SearchScope } from '@/lib/enums/searchScope';
import { useTRPC } from '@/trpc/client';
import { searchMoreQueryOptions } from '@/lib/queries/searchQueries';
import { useToaster } from '@/components/hooks/useToaster';

const SearchCompaniesContent: React.FC<CompanySearchResult> = ({ items, pagination }) => {
  const [currentItems, setCurrentItems] = useState(items);
  const [currentPagination, setCurrentPagination] = useState(pagination);
  const numberOfPages = Math.ceil(currentPagination.total / currentPagination.limit);
  const currentPage = Math.floor(currentPagination.from / currentPagination.limit) + 1;
  const search = useSearchParams();
  const { t } = useTranslation();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { openToast } = useToaster();

  React.useEffect(() => {
    setCurrentItems(items);
    setCurrentPagination(pagination);
  }, [items, pagination]);

  const handlePageChangeAsync = async (selected: number) => {
    const selectedFrom = selected * currentPagination.limit;
    try {
      const result = await queryClient.fetchQuery(
        searchMoreQueryOptions({
          trpc,
          query: search.get('query') ?? undefined,
          scope: SearchScope.companies,
          from: selectedFrom,
          limit: currentPagination.limit,
        }),
      );
      setCurrentItems(result.companies.items);
      setCurrentPagination(result.companies.pagination);
    } catch (error) {
      console.error('failed to load company search page', error);
      openToast({
        id: 'search-page-load-failed',
        text: t('components.error.title'),
        description: t('components.error.description'),
        intent: 'danger',
      });
    }
  };

  const handlePageChange = ({ selected }: { selected: number }) => {
    void handlePageChangeAsync(selected);
  };

  return (
    <React.Fragment>
      <table className="wrappable">
        <thead>
          <tr>
            <th>{t('components.search.companyName')}</th>
            <th className="align-right">{t('components.search.registrationCode')}</th>
          </tr>
        </thead>
        <tbody>
          <SearchCompaniesList companies={currentItems} />
        </tbody>
      </table>
      <Paginator
        numberOfPages={numberOfPages}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </React.Fragment>
  );
};

export default SearchCompaniesContent;
